<template>
  <b-modal v-model="show" size="lg" centered hide-header hide-footer
           modal-class="task-modal" body-class="task-modal__body" @hidden="onHidden">
    <div class="tk" v-if="show">
      <!-- Hero header -->
      <div class="tk__hero" :style="{ background: heroBg }">
        <div class="tk__hero-icon"><i :class="metaCur.icon"></i></div>
        <div class="tk__hero-text">
          <div class="tk__hero-kicker">{{ $t('taskTypes.' + curType) }}</div>
          <h4 class="tk__hero-title" v-if="mode === 'view'">{{ task.title }}</h4>
          <h4 class="tk__hero-title" v-else>{{ isNew ? $t('tasks.newTask') : $t('tasks.editTask') }}</h4>
        </div>
        <button type="button" class="tk__close" @click="show = false"><i class="fa-solid fa-xmark"></i></button>
      </div>

      <b-overlay :show="loading" rounded opacity="0.4" spinner-variant="primary">
        <!-- ============ FORM (new / edit) ============ -->
        <div v-if="mode !== 'view'" class="tk__content">
          <div class="tk__field">
            <label class="tk__label">{{ $t('columns.type') }}</label>
            <v-select v-model="form.type" :options="typeOptions" :reduce="o => o.value" label="label" :clearable="false">
              <template #option="o"><i :class="o.icon" class="mr-50" :style="{ color: o.color }"></i>{{ o.label }}</template>
              <template #selected-option="o"><i :class="o.icon" class="mr-50" :style="{ color: o.color }"></i>{{ o.label }}</template>
            </v-select>
          </div>
          <div class="tk__field">
            <label class="tk__label">{{ $t('columns.title') }}</label>
            <b-form-input v-model="form.title" :placeholder="$t('tasks.titlePlaceholder')"/>
          </div>
          <div class="tk__field">
            <label class="tk__label">{{ $t('tasks.description') }}</label>
            <b-form-textarea v-model="form.description" rows="2" :placeholder="$t('tasks.descPlaceholder')"/>
          </div>
          <div v-if="typeModule(form.type) === 'CLIENT'" class="tk__field">
            <label class="tk__label">{{ $t('tasks.client') }}</label>
            <client-picker v-model="form.relatedId"/>
          </div>
          <div class="tk__grid">
            <div class="tk__field">
              <label class="tk__label">{{ $t('columns.assignee') }}</label>
              <user-select-box v-model="form.assigneeUserId" :clearable="true"/>
            </div>
            <div class="tk__field">
              <label class="tk__label">{{ $t('columns.priority') }}</label>
              <v-select v-model="form.priority" :options="priorityOptions" :reduce="o => o.value" label="label" :clearable="false"/>
            </div>
            <div class="tk__field">
              <label class="tk__label">{{ $t('columns.dueDate') }}</label>
              <b-form-datepicker v-model="form.dueDate" :locale="$i18n.locale" reset-button
                                 :date-format-options="{ year: 'numeric', month: '2-digit', day: '2-digit' }"/>
            </div>
            <div class="tk__field">
              <label class="tk__label">{{ $t('tasks.watchers') }}</label>
              <user-select-box v-model="form.watchers" multi :clearable="true"/>
            </div>
          </div>
          <div class="tk__footer">
            <b-button variant="outline-secondary" @click="onCancel">
              {{ $t('operations.cancel') }}
            </b-button>
            <b-button variant="primary" :disabled="!form.title || saving" @click="save">
              <b-spinner small v-if="saving" class="mr-50"/>{{ isNew ? $t('tasks.assignTask') : $t('operations.save') }}
            </b-button>
          </div>
        </div>

        <!-- ============ DETAIL (view) ============ -->
        <div v-else class="tk__content">
          <!-- status + meta -->
          <div class="tk__status-row">
            <button v-for="s in boardStatuses" :key="s" type="button"
                    :class="['tk__status-pill', 's-' + s.toLowerCase(), { 'is-active': task.status === s }]"
                    @click="setStatus(s)">
              <span class="tk__status-dot"></span>{{ $t('taskStatuses.' + s) }}
            </button>
            <b-dropdown size="sm" variant="flat-secondary" right no-caret class="ml-auto">
              <template #button-content><i class="fa-solid fa-ellipsis"></i></template>
              <b-dropdown-item v-if="canEdit" @click="enterEdit"><i class="fa-solid fa-pen mr-50"></i>{{ $t('operations.edit') }}</b-dropdown-item>
              <b-dropdown-item @click="setStatus('CANCELLED')">{{ $t('taskStatuses.CANCELLED') }}</b-dropdown-item>
              <b-dropdown-item v-if="canDelete" @click="remove" variant="danger"><i class="fa-solid fa-trash mr-50"></i>{{ $t('operations.delete') }}</b-dropdown-item>
            </b-dropdown>
          </div>

          <div class="tk__meta">
            <div><span class="tk__meta-l">{{ $t('columns.assignee') }}</span><span>{{ task.assigneeName || $t('tasks.unassigned') }}</span></div>
            <div><span class="tk__meta-l">{{ $t('columns.priority') }}</span><b-badge :variant="priorityVariant(task.priority)">{{ $t('taskPriorities.' + task.priority) }}</b-badge></div>
            <div v-if="task.dueDate"><span class="tk__meta-l">{{ $t('columns.dueDate') }}</span><span :class="{ 'text-danger': overdue }">{{ task.dueDate | momentDate }}</span></div>
            <div><span class="tk__meta-l">{{ $t('columns.createdBy') }}</span><span>{{ task.createdByName }}</span></div>
          </div>

          <div v-if="task.description" class="tk__desc">{{ task.description }}</div>

          <!-- Type-specific body: open the linked entity (clients/leads/txn).
               Report tasks don't show this — the live client list below is the
               point, and it works without dashboard permission. -->
          <div v-if="relatedRouteCur && task.type !== 'REVIEW_CLIENT_REPORT'" class="tk__entity">
            <b-button variant="outline-primary" @click="openEntity">
              <i :class="metaCur.icon" class="mr-50"></i>{{ $t('tasks.openEntity') }}
            </b-button>
          </div>

          <!-- Flagship: live client report list -->
          <div v-if="task.type === 'REVIEW_CLIENT_REPORT'" class="tk__report">
            <div class="tk__report-head">
              <span><i class="fa-solid fa-list-check mr-50"></i><strong>{{ reportTotal }}</strong> {{ $t('tasks.clientsToHandle') }}</span>
              <span class="tk__report-range" v-if="reportTotal">{{ reportRangeLabel }}</span>
            </div>
            <b-overlay :show="reportLoading" rounded opacity="0.4">
              <b-table small responsive :items="reportClients" :fields="reportFields" class="tk__report-table mb-0"
                       show-empty @row-clicked="openClient">
                <template #cell(name)="d">{{ [d.item.firstName, d.item.lastName].filter(Boolean).join(' ') || d.item.id }}</template>
                <template #cell(lastNoteTime)="d"><span v-if="d.value">{{ d.value | momentDate }}</span><span v-else class="text-muted">—</span></template>
                <template #cell(open)><i class="fa-solid fa-arrow-up-right-from-square text-primary"></i></template>
                <template #empty><div class="text-center text-muted py-2">{{ $t('clientDashboard.noClients') }}</div></template>
              </b-table>
            </b-overlay>
            <div class="tk__report-foot" v-if="reportTotal > reportSize">
              <b-pagination v-model="reportPage" :total-rows="reportTotal" :per-page="reportSize"
                            size="sm" class="mb-0" @change="onReportPage"/>
              <b-form-select v-model="reportSize" size="sm" :options="[50, 100, 250]" style="width: 80px" @change="onReportSize"/>
            </div>
          </div>

          <!-- Comments -->
          <div class="tk__comments">
            <div class="tk__label mb-50">{{ $t('tasks.comments') }}</div>
            <div v-if="!task.comments || !task.comments.length" class="text-muted small mb-1">{{ $t('tasks.noComments') }}</div>
            <div v-for="(c, i) in (task.comments || [])" :key="i" class="tk__comment">
              <b-avatar size="26" variant="light-primary" :text="initials(c.userName)"/>
              <div class="tk__comment-body">
                <div class="tk__comment-head"><strong>{{ c.userName }}</strong><span class="text-muted ml-50">{{ c.timestamp | momentDateTime }}</span></div>
                <div>{{ c.message }}</div>
              </div>
            </div>
            <b-input-group class="mt-50">
              <b-form-input v-model="newComment" :placeholder="$t('tasks.addComment')" @keyup.enter="postComment"/>
              <b-input-group-append>
                <b-button variant="primary" :disabled="!newComment || posting" @click="postComment">{{ $t('tasks.send') }}</b-button>
              </b-input-group-append>
            </b-input-group>
          </div>
        </div>
      </b-overlay>
    </div>
  </b-modal>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import mixinNotifications from '@/components/mixin/mixinNotifications'
import mixinPermissions from '@/components/mixin/mixinPermissions'
import UserSelectBox from '@/components/widget/select/UserSelectBox'
import ClientPicker from '@/components/widget/picker/clientPicker'
import TaskRequest from '@/model/task/TaskRequest'
import { taskMeta, relatedRoute, TASK_TYPES, TASK_PRIORITIES, BOARD_STATUSES, PRIORITY_VARIANT } from '@/model/task/taskTypeMeta'

export default {
  name: 'TaskModal',
  components: { UserSelectBox, ClientPicker },
  mixins: [mixinNotifications, mixinPermissions],
  data: () => ({
    show: false,
    loading: false,
    saving: false,
    posting: false,
    mode: 'view', // 'new' | 'view' | 'edit'
    task: {},
    form: TaskRequest(),
    newComment: '',
    boardStatuses: BOARD_STATUSES,
    reportClients: [],
    reportTotal: 0,
    reportLoading: false,
    reportPage: 1,
    reportSize: 50,
  }),
  computed: {
    ...mapGetters('user', ['user']),
    isNew() { return this.mode === 'new' },
    curType() { return this.mode === 'view' ? (this.task.type || 'GENERAL') : (this.form.type || 'GENERAL') },
    metaCur() { return taskMeta(this.curType) },
    heroBg() {
      const c = this.metaCur.color
      return `linear-gradient(135deg, ${c} 0%, ${c}cc 100%)`
    },
    typeOptions() { return TASK_TYPES.map(t => ({ value: t, label: this.$t('taskTypes.' + t), icon: taskMeta(t).icon, color: taskMeta(t).color })) },
    priorityOptions() { return TASK_PRIORITIES.map(p => ({ value: p, label: this.$t('taskPriorities.' + p) })) },
    // Only the task's creator may edit it.
    canEdit() { return !!(this.user && this.task && this.task.createdBy === this.user.id) },
    canDelete() { return this.hasPermission(this.permissions.TASK_DELETE) },
    relatedRouteCur() { return this.mode === 'view' ? relatedRoute(this.task) : null },
    overdue() { return this.task.dueDate && this.task.status !== 'DONE' && this.$moment(this.task.dueDate).isBefore(this.$moment()) },
    reportRangeLabel() {
      if (!this.reportTotal) return ''
      const from = (this.reportPage - 1) * this.reportSize + 1
      const to = Math.min(this.reportPage * this.reportSize, this.reportTotal)
      return `${from}–${to} / ${this.reportTotal}`
    },
    reportFields() {
      return [
        { key: 'name', label: this.$t('clientDashboard.client') },
        { key: 'email', label: this.$t('columns.email') },
        { key: 'country', label: this.$t('columns.country') },
        { key: 'lastNoteTime', label: this.$t('clientDashboard.lastNote') },
        { key: 'open', label: '', class: 'text-right' },
      ]
    },
  },
  methods: {
    ...mapActions('tasks', ['getTaskById', 'createTask', 'updateTask', 'changeTaskStatus', 'addTaskComment', 'getTaskReportClients', 'deleteTask']),
    onCancel() { if (this.isNew) { this.show = false } else { this.mode = 'view' } },
    remove() {
      this.$bvModal.msgBoxConfirm(this.$t('tasks.deleteConfirm'), {
        okVariant: 'danger', okTitle: this.$t('operations.delete'), cancelTitle: this.$t('operations.cancel'), centered: true,
      }).then(ok => {
        if (!ok) return
        this.deleteTask(this.task.id).then(
          () => { this.showSuccessNotification(this.$t('tasks.taskDeleted')); this.show = false; this.$emit('submitted') },
          err => this.showServerErrorNotification('deleteTask', 'tasks', err)
        )
      })
    },
    typeModule(type) { return taskMeta(type).module },
    priorityVariant(p) { return PRIORITY_VARIANT[p] || 'light-secondary' },
    initials(n) { return (n || '').split(' ').filter(Boolean).slice(0, 2).map(s => s[0]).join('').toUpperCase() },
    showModal(idOrNull, presets) {
      this.reportClients = []; this.reportTotal = 0; this.reportPage = 1; this.newComment = ''
      if (!idOrNull) {
        this.mode = 'new'
        this.task = {}
        this.form = TaskRequest(presets || {})
        this.show = true
        return
      }
      this.mode = 'view'
      this.show = true
      this.load(idOrNull)
    },
    load(id) {
      this.loading = true
      this.getTaskById(id).then(
        t => {
          this.task = t || {}
          if (t && t.type === 'REVIEW_CLIENT_REPORT') this.loadReport()
        },
        err => this.showServerErrorNotification('getTaskById', 'tasks', err)
      ).finally(() => { this.loading = false })
    },
    loadReport() {
      this.reportLoading = true
      this.getTaskReportClients({ id: this.task.id, page: this.reportPage, size: this.reportSize }).then(
        res => { this.reportClients = res.data || []; this.reportTotal = (res.metadata && res.metadata.total != null) ? res.metadata.total : this.reportClients.length },
        () => {}
      ).finally(() => { this.reportLoading = false })
    },
    onReportPage(page) { this.reportPage = page; this.$nextTick(this.loadReport) },
    onReportSize() { this.reportPage = 1; this.$nextTick(this.loadReport) },
    enterEdit() {
      this.form = TaskRequest({
        ...this.task,
        dueDate: this.task.dueDate ? String(this.task.dueDate).substring(0, 10) : null,
      })
      this.mode = 'edit'
    },
    buildRequest() {
      const req = { ...this.form }
      if (req.dueDate && req.dueDate.length === 10) req.dueDate = req.dueDate + ' 23:59'
      req.relatedModule = this.typeModule(req.type)
      return req
    },
    save() {
      this.saving = true
      const payload = this.buildRequest()
      const op = this.isNew
        ? this.createTask({ task: payload })
        : this.updateTask({ id: this.task.id, task: payload })
      op.then(
        saved => {
          this.showSuccessNotification(this.$t(this.isNew ? 'tasks.taskCreated' : 'tasks.taskUpdated'))
          this.task = saved
          this.mode = 'view'
          this.$emit('submitted')
          if (this.isNew) this.show = false
        },
        err => this.showServerErrorNotification('saveTask', 'tasks', err)
      ).finally(() => { this.saving = false })
    },
    setStatus(status) {
      if (this.task.status === status) return
      this.changeTaskStatus({ id: this.task.id, status }).then(
        saved => { this.task = saved; this.$emit('submitted') },
        err => this.showServerErrorNotification('changeTaskStatus', 'tasks', err)
      )
    },
    postComment() {
      if (!this.newComment) return
      this.posting = true
      this.addTaskComment({ id: this.task.id, message: this.newComment }).then(
        saved => { this.task = saved; this.newComment = '' },
        err => this.showServerErrorNotification('addTaskComment', 'tasks', err)
      ).finally(() => { this.posting = false })
    },
    openEntity() {
      const route = this.relatedRouteCur
      if (route) window.open(this.$router.resolve(route).href, '_blank')
    },
    openClient(item) {
      if (item && item.id) window.open(this.$router.resolve({ name: 'client', params: { clientId: item.id } }).href, '_blank')
    },
    onHidden() { this.mode = 'view'; this.task = {}; this.reportClients = [] },
  },
}
</script>

<style lang="scss">
.task-modal {
  // no overflow:hidden — it would clip the date-picker dropdown; the hero
  // rounds its own top corners instead.
  .modal-content { border: none; border-radius: 18px; box-shadow: 0 24px 60px -20px rgba(20,24,50,0.45); }
  .modal-body.task-modal__body { padding: 0; }
}
.tk {
  &__hero { display: flex; align-items: center; gap: 1rem; padding: 1.1rem 1.4rem; color: #fff; border-radius: 18px 18px 0 0; }
  &__hero-icon { display: flex; align-items: center; justify-content: center; width: 46px; height: 46px; border-radius: 13px; font-size: 1.25rem; background: rgba(255,255,255,0.22); flex-shrink: 0; }
  &__hero-text { flex: 1; min-width: 0; }
  &__hero-kicker { font-size: 0.68rem; text-transform: uppercase; letter-spacing: 0.06em; opacity: 0.9; font-weight: 700; }
  &__hero-title { margin: 0; font-weight: 700; font-size: 1.2rem; }
  &__close { border: none; background: rgba(255,255,255,0.18); color: #fff; width: 32px; height: 32px; border-radius: 9px; cursor: pointer; transition: all .2s ease; &:hover { background: rgba(255,255,255,0.32); transform: rotate(90deg); } }

  &__content { padding: 1.2rem 1.4rem; display: flex; flex-direction: column; gap: 0.85rem; }
  &__grid { display: grid; grid-template-columns: 1fr 1fr; gap: 0.85rem; }
  &__label { font-size: 0.72rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.03em; color: #6e6b7b; margin-bottom: 0.3rem; display: block; }
  &__footer { display: flex; justify-content: flex-end; gap: 0.6rem; margin-top: 0.4rem; }

  &__status-row { display: flex; align-items: center; gap: 0.5rem; }
  &__status-pill {
    display: inline-flex; align-items: center; gap: 0.4rem;
    border: 1px solid #e4e6f0; background: #fff; color: #8a90a2; font-weight: 600;
    font-size: 0.78rem; padding: 0.4rem 0.95rem; border-radius: 20px; cursor: pointer;
    transition: all .15s ease;
  }
  &__status-pill:hover { border-color: #c5cadd; color: #6e6b7b; }
  &__status-dot { width: 8px; height: 8px; border-radius: 50%; background: #c5cadd; transition: background .15s ease; }
  &__status-pill.s-new .tk__status-dot { background: #00cfe8; }
  &__status-pill.s-in_progress .tk__status-dot { background: #ff9f43; }
  &__status-pill.s-done .tk__status-dot { background: #28c76f; }
  // selected — clearly highlighted: filled with its status colour, white text, lifted
  &__status-pill.is-active {
    color: #fff; border-color: transparent; font-weight: 700;
    transform: translateY(-1px);
    box-shadow: 0 6px 14px -5px rgba(20, 24, 50, 0.45);
    .tk__status-dot { background: rgba(255, 255, 255, 0.9); }
  }
  &__status-pill.is-active.s-new { background: #00cfe8; }
  &__status-pill.is-active.s-in_progress { background: #ff9f43; }
  &__status-pill.is-active.s-done { background: #28c76f; }

  &__meta { display: grid; grid-template-columns: 1fr 1fr; gap: 0.4rem 1rem; background: #f7f8fc; border-radius: 12px; padding: 0.8rem 1rem; font-size: 0.85rem; }
  &__meta-l { display: block; font-size: 0.68rem; text-transform: uppercase; letter-spacing: 0.03em; color: #a0a4b8; font-weight: 600; }
  &__desc { font-size: 0.9rem; color: #4b4b63; white-space: pre-wrap; }
  &__entity { }
  &__report-head { margin-bottom: 0.5rem; font-size: 0.9rem; display: flex; align-items: center; justify-content: space-between; gap: 0.5rem; }
  &__report-range { font-size: 0.78rem; color: #a0a4b8; font-weight: 600; }
  &__report-table { font-size: 0.82rem; tbody tr { cursor: pointer; } }
  &__report-foot { display: flex; align-items: center; justify-content: space-between; gap: 0.5rem; margin-top: 0.6rem; }
  &__comments { border-top: 1px solid rgba(110,117,159,0.12); padding-top: 0.8rem; }
  &__comment { display: flex; gap: 0.6rem; margin-bottom: 0.6rem; }
  &__comment-body { font-size: 0.85rem; }
  &__comment-head { font-size: 0.8rem; }

  @media (max-width: 575px) { &__grid, &__meta { grid-template-columns: 1fr; } }
}
// keep the date label on one line inside the narrow field
.task-modal .b-form-datepicker > label.form-control {
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis; height: auto;
}
body.dark-layout {
  .task-modal .modal-content { background: var(--crm-dark-surface, #232c3e); color: #d0d2d6; }
  .tk__meta { background: var(--crm-dark-surface-2, #2e3850); }
  .tk__status-pill { background: var(--crm-dark-surface-2, #2e3850); border-color: rgba(255,255,255,0.1); color: #b4b7bd; }
  .tk__desc { color: #b4b7bd; }
}
</style>
